/** 当前登录用户与权限工具 */

const POPUP_BRIDGE_KEY = 'yn_user_bridge'

/** 打开 Word 弹窗前，把登录态写入 localStorage 供新窗口读取 */
export function bridgeSessionForPopup() {
  try {
    const raw = sessionStorage.getItem('yn_user')
    if (raw) localStorage.setItem(POPUP_BRIDGE_KEY, raw)
  } catch (e) { /* ignore */ }
}

/** 弹窗页从父窗口或 bridge 恢复登录态（sessionStorage 不跨窗口共享） */
export function inheritSessionForPopup() {
  if (sessionStorage.getItem('yn_user')) return true
  try {
    if (window.opener && !window.opener.closed) {
      const raw = window.opener.sessionStorage.getItem('yn_user')
      if (raw) {
        sessionStorage.setItem('yn_user', raw)
        return true
      }
    }
  } catch (e) { /* ignore */ }
  try {
    const raw = localStorage.getItem(POPUP_BRIDGE_KEY)
    if (raw) {
      sessionStorage.setItem('yn_user', raw)
      return true
    }
  } catch (e) { /* ignore */ }
  return false
}

/** Word 弹窗页关闭后清理 bridge */
export function clearPopupSessionBridge() {
  try { localStorage.removeItem(POPUP_BRIDGE_KEY) } catch (e) { /* ignore */ }
}

export function getCurrentUser() {
  try {
    return JSON.parse(sessionStorage.getItem('yn_user') || '{}')
  } catch (e) {
    return {}
  }
}

export function isLoggedIn() {
  const u = getCurrentUser()
  return !!(u && u.id)
}

export function setCurrentUser(user) {
  sessionStorage.setItem('yn_user', JSON.stringify(user || {}))
}

export function userParams(extra = {}) {
  const u = getCurrentUser()
  return u.id ? { ...extra, yhid: u.id, cur_yhid: u.id } : { ...extra }
}

export function isAdmin(user) {
  const u = user || getCurrentUser()
  const codes = String(u.jsdm_ls || '')
  if (codes.split(',').map(s => s.trim()).includes('admin')) return true
  return String(u.jsmc_ls || '').includes('系统管理员')
}

function roleCodesOf(user) {
  return String((user && user.jsdm_ls) || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
}

/** 综合部主任 / 总经理（派遣、接样） */
export function isTaskDispatcher(user) {
  const u = user || getCurrentUser()
  const codes = roleCodesOf(u)
  if (codes.includes('office_dir') || codes.includes('ceo') || codes.includes('admin')) return true
  if (u.app && u.app.webDispatch) return true
  const role = String(u.jsmc_ls || '')
  return /综合部主任|总经理|任务分配人/.test(role)
}

/** 现场部主任 */
export function isFieldDirector(user) {
  const u = user || getCurrentUser()
  const codes = roleCodesOf(u)
  if (codes.includes('field_dir')) return true
  return /现场部主任/.test(String(u.jsmc_ls || ''))
}

/** 综合部主任 */
export function isOfficeDirector(user) {
  const u = user || getCurrentUser()
  const codes = roleCodesOf(u)
  if (codes.includes('office_dir')) return true
  return /综合部主任/.test(String(u.jsmc_ls || ''))
}

/** 登录后默认首页 */
export function resolveDefaultHomeRoute(user) {
  const u = user || getCurrentUser()
  if (isAdmin(u)) return '/home'
  const field = isFieldDirector(u)
  const office = isOfficeDirector(u) || isTaskDispatcher(u)
  if (field && !office) return '/home/field'
  if (office && !field) return '/home/office'
  return '/home'
}

/** 周期任务安排接收人（现场部等） */
export function isRoutineReceiver(user) {
  const u = user || getCurrentUser()
  if (isAdmin(u) || isTaskDispatcher(u)) return true
  const codes = roleCodesOf(u)
  if (codes.includes('field_dir') || codes.includes('sampler')) return true
  const role = String(u.jsmc_ls || '')
  return /现场|采样/.test(role)
}

/** Web 样品管理：综合部主任 / 总经理（按职务能力，不写死账号） */
export function canViewHandoverMenu(user) {
  const u = user || getCurrentUser()
  if (isAdmin(u) || isTaskDispatcher(u)) return true
  if (u.app && u.app.handoverReceive) return true
  const role = String(u.jsmc_ls || '')
  return /综合部主任|总经理/.test(role)
}

/** 理化中心分析员 / 技术负责人 — 领样、理化录入 */
export function isLabAnalyst(user) {
  const u = user || getCurrentUser()
  if (isAdmin(u)) return true
  const codes = roleCodesOf(u)
  if (codes.includes('analyst') || codes.includes('tech_dir')) return true
  if (u.app && u.app.webLab) return true
  return /分析员|理化中心主任|技术负责人|分析人/.test(String(u.jsmc_ls || ''))
}

/** 检测任务安排：理化主任 / 分析员（按职务，不写死账号） */
export function canViewArrangeMenu(user) {
  const u = user || getCurrentUser()
  if (isAdmin(u) || isLabAnalyst(u)) return true
  const role = String(u.jsmc_ls || '')
  return /理化|分析/.test(role)
}

/** 可担任采样负责人/组员：采样员、现场部主任 */
export function isPickSampler(user) {
  if (!user) return false
  const codes = roleCodesOf(user)
  if (codes.includes('sampler') || codes.includes('field_dir')) return true
  const role = String(user.jsmc_ls || '')
  if (/采样员|现场部主任|检测人员/.test(role)) return true
  if (user.app && (user.app.soilSample || user.app.legacyTask)) return true
  return false
}

export function canViewAllRecords(user) {
  return isAdmin(user) || isTaskDispatcher(user) || isRoutineReceiver(user)
}

export function canEditRecord(row, user) {
  const u = user || getCurrentUser()
  if (!u.id || !row) return false
  if (isAdmin(u) || isTaskDispatcher(u) || isRoutineReceiver(u)) return true
  if (String(row.cjr_id || '') === String(u.id)) return true
  try {
    const sj = typeof row.sj_json === 'string' ? JSON.parse(row.sj_json || '{}') : (row.sj_json || {})
    if (sj.cyry && String(sj.cyry) === String(u.xm)) return true
  } catch (e) { /* ignore */ }
  return false
}

/** 周期任务安排：仅接收人（assignee）或管理员可编辑 */
export function canEditRoutinePlan(project, user) {
  const u = user || getCurrentUser()
  if (!u.id || !project) return false
  if (isAdmin(u)) return true
  const aid = parseInt(project.assignee_id, 10)
  return aid > 0 && parseInt(u.id, 10) === aid
}

/** signRole 与 footer fk 映射（056/135 等） */
export const SIGN_ROLE_FK_MAP = {
  '交样人': 'jyr',
  '样品管理员/接样人': 'jjy',
  '接样人': 'jjy',
  '派遣人': 'pqr',
  '批准人': 'pzr'
}

export function signPrefixForRole(role) {
  const fk = SIGN_ROLE_FK_MAP[role]
  if (fk) return fk
  return '_sign_' + role
}

export function signKeysForRole(role) {
  const prefix = signPrefixForRole(role)
  return {
    yhid: prefix + '_yhid',
    xm: prefix + '_xm',
    img: prefix + '_img',
    legacy: fkLegacyKey(role)
  }
}

function fkLegacyKey(role) {
  return SIGN_ROLE_FK_MAP[role] || null
}

export function resolveAssetUrl(path) {
  if (!path) return ''
  const s = String(path).trim()
  if (/^https?:\/\//i.test(s)) return s
  const base = (process.env.VUE_APP_API_BASE || '').replace(/\/$/, '')
  const rel = s.startsWith('/') ? s : '/' + s
  return base ? base + rel : rel
}

export function readSignValue(data, role) {
  const keys = signKeysForRole(role)
  const yhid = data[keys.yhid]
  const xm = data[keys.xm] || (keys.legacy ? data[keys.legacy] : '')
  const img = data[keys.img]
  if (!yhid && !xm && !img) return null
  return { yhid, xm, img }
}

export function writeSignValue(data, role, sign) {
  const out = { ...data }
  const keys = signKeysForRole(role)
  if (!sign) {
    delete out[keys.yhid]
    delete out[keys.xm]
    delete out[keys.img]
    if (keys.legacy) out[keys.legacy] = ''
    delete out['_sign_' + role + '_yhid']
    delete out['_sign_' + role + '_xm']
    delete out['_sign_' + role + '_img']
    return out
  }
  out[keys.yhid] = sign.yhid
  out[keys.xm] = sign.xm
  out[keys.img] = sign.img
  if (keys.legacy) out[keys.legacy] = sign.xm || ''
  out['_sign_' + role + '_yhid'] = sign.yhid
  out['_sign_' + role + '_xm'] = sign.xm
  out['_sign_' + role + '_img'] = sign.img
  return out
}
